'use client';
import React, { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MapPin, ChevronRight, Heart, Brain, Bone, HeartHandshake, Baby, Sparkles, Activity, Droplets, Eye, Ear, Smile, Ribbon, Leaf, Stethoscope, AlertCircle, Building2, UserRound,  } from 'lucide-react';
import TopNav from '@/components/layout/TopNav';
import LocationFinder from '@/components/locations/LocationFinder';
import { useSelectedCentre } from '@/context/SelectedCentreContext';
import { centres, careCategories, officialDepartments, isDepartmentAvailableAtCentre, type Centre, type OfficialDepartment, type CareCategory,  } from '@/data/locations';
import { doctors } from '@/data/doctors';
import { trackEvent } from '@/lib/analytics';

type FindDoctorStep = 'select-centre' | 'select-category' | 'select-department' | 'select-doctor' | 'not-available';

const categoryIconMap: Record<string, React.ElementType> = {
  'heart-chest': Heart,
  'brain-spine': Brain,
  'bones-joints': Bone,
  'womens-health': HeartHandshake,
  'child-health': Baby,
  'skin-hair': Sparkles,
  'digestive-health': Activity,
  'diabetes-hormonal': Droplets,
  'eye-vision': Eye,
  'ent-hearing': Ear,
  'mental-wellbeing': Brain,
  'dental-health': Smile,
  'cancer-care': Ribbon,
  'nutrition-wellness': Leaf,
  'general-health': Stethoscope,
};

const categoryColorMap: Record<string, { text: string; border: string; bg: string }> = {
  'heart-chest': { text: 'text-rose-400', border: 'border-rose-500/30', bg: 'bg-rose-500/8' },
  'brain-spine': { text: 'text-indigo-400', border: 'border-indigo-500/30', bg: 'bg-indigo-500/8' },
  'bones-joints': { text: 'text-slate-300', border: 'border-slate-400/30', bg: 'bg-slate-500/8' },
  'womens-health': { text: 'text-pink-400', border: 'border-pink-500/30', bg: 'bg-pink-500/8' },
  'child-health': { text: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/8' },
  'skin-hair': { text: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/8' },
  'digestive-health': { text: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/8' },
  'diabetes-hormonal': { text: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/8' },
  'eye-vision': { text: 'text-teal-400', border: 'border-teal-500/30', bg: 'bg-teal-500/8' },
  'ent-hearing': { text: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500/8' },
  'mental-wellbeing': { text: 'text-violet-400', border: 'border-violet-500/30', bg: 'bg-violet-500/8' },
  'dental-health': { text: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/8' },
  'cancer-care': { text: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/8' },
  'nutrition-wellness': { text: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/8' },
  'general-health': { text: 'text-sky-400', border: 'border-sky-500/30', bg: 'bg-sky-500/8' },
};

const panelVariants = {
  enter: { opacity: 0, x: 24, scale: 0.98 },
  center: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, x: -24, scale: 0.98, transition: { duration: 0.22 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.05, duration: 0.35, ease: 'easeOut' },
  }),
};

// Map department IDs from locations.ts to doctors.ts department field
const deptIdToDoctorDept: Record<string, string> = {
  'cardiology': 'cardiology',
  'cardio-thoracic-surgery': 'cardiology',
  'neuro-spine-surgery': 'neuro-spine',
  'orthopaedics': 'orthopaedics',
  'gynaecology': 'gynaecology',
  'paediatrics': 'paediatrics',
  'general-medicine': 'general-medicine',
  'internal-medicine': 'general-medicine',
  'general-surgery': 'general-surgery',
  'urology': 'urology',
  'ophthalmology': 'ophthalmology',
  'ent': 'ent',
  'dermatology': 'dermatology',
  'psychiatry': 'psychiatry',
  'radiology': 'radiology',
  'dental': 'dental',
  'nutrition-diet': 'nutrition',
  'physiotherapy': 'physiotherapy',
  'oncology': 'oncology',
  'endocrinology': 'general-medicine',
  'gastroenterology': 'general-medicine',
  'rheumatology': 'orthopaedics',
  'plastic-surgery': 'plastic-surgery',
  'anaesthesia-pain': 'general-medicine',
  'blood-bank': 'pathology',
};

export default function FindDoctorContent() {
  const router = useRouter();
  const { selectedCentre, setSelectedCentre } = useSelectedCentre();
  const [step, setStep] = useState<FindDoctorStep>(
    selectedCentre ? 'select-category' : 'select-centre'
  );
  const [locationOpen, setLocationOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CareCategory | null>(null);
  const [selectedDept, setSelectedDept] = useState<OfficialDepartment | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    trackEvent({ event: 'doctor_journey_started', source: 'find-doctor-page' });
    return () => clearTimeout(t);
  }, []);

  const handleSelectCentre = useCallback((centre: Centre) => {
    setSelectedCentre(centre);
    setStep('select-category');
    trackEvent({ event: 'concern_selected', source: `centre:${centre.id}` });
  }, [setSelectedCentre]);

  const handleSelectCategory = useCallback((cat: CareCategory) => {
    setSelectedCategory(cat);
    setStep('select-department');
    trackEvent({ event: 'health_concern_selected', source: cat.id });
  }, []);

  const handleSelectDept = useCallback((dept: OfficialDepartment) => {
    if (!selectedCentre) return;
    if (!isDepartmentAvailableAtCentre(selectedCentre.id, dept.id)) {
      setSelectedDept(dept);
      setStep('not-available');
      return;
    }
    setSelectedDept(dept);
    setStep('select-doctor');
    trackEvent({ event: 'question_answered', step: 3 });
  }, [selectedCentre]);

  const handleBack = useCallback(() => {
    if (step === 'select-centre') router.push('/digital-reception-screen');
    else if (step === 'select-category') setStep('select-centre');
    else if (step === 'select-department') setStep('select-category');
    else if (step === 'select-doctor') setStep('select-department');
    else if (step === 'not-available') setStep('select-department');
  }, [step, router]);

  // Get available departments for selected category at selected centre
  const availableDepts = selectedCategory && selectedCentre
    ? selectedCategory.departmentIds
        .map((id) => officialDepartments.find((d) => d.id === id))
        .filter(Boolean) as OfficialDepartment[]
    : [];

  // Get doctors for selected department
  const deptDoctors = selectedDept
    ? doctors.filter((d) => d.department === (deptIdToDoctorDept[selectedDept.id] ?? selectedDept.id))
    : [];

  const stepLabels: Record<FindDoctorStep, string> = {
    'select-centre': 'STEP 1 — SELECT CENTRE',
    'select-category': 'STEP 2 — WHAT DO YOU NEED HELP WITH?',
    'select-department': 'STEP 3 — SELECT DEPARTMENT',
    'select-doctor': 'STEP 4 — SELECT DOCTOR',
    'not-available': 'SERVICE AVAILABILITY',
  };

  return (
    <main
      className="journey-bg min-h-screen flex flex-col pb-24 md:pb-12 overflow-x-hidden"
      role="main"
      aria-label="Find a Doctor"
    >
      <TopNav showBackButton backHref="/digital-reception-screen" backLabel="Reception" />

      <div className={`flex-1 flex flex-col items-center justify-start pt-24 px-4 md:px-8 transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="w-full max-w-2xl">

          {/* Step label */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors tracking-wider uppercase"
                aria-label="Go back"
              >
                <ArrowLeft size={13} />
                BACK
              </button>
              <span className="text-xs text-primary/60 font-semibold tracking-widest uppercase">
                {stepLabels[step]}
              </span>
            </div>
            {selectedCentre && step !== 'select-centre' && (
              <button
                onClick={() => setLocationOpen(true)}
                className="flex items-center gap-1.5 text-xs text-primary/70 hover:text-primary transition-colors"
                aria-label="Change selected centre"
              >
                <MapPin size={11} />
                <span className="hidden sm:inline">{selectedCentre.shortName}</span>
                <span className="text-primary/40">CHANGE</span>
              </button>
            )}
          </div>

          <AnimatePresence mode="wait">
            {/* STEP 1: SELECT CENTRE */}
            {step === 'select-centre' && (
              <motion.div key="select-centre" variants={panelVariants} initial="enter" animate="center" exit="exit">
                <div className="mb-8">
                  <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2">
                    SELECT YOUR CENTRE
                  </h1>
                  <p className="text-sm text-white/50">
                    Choose the All Is Well centre you&apos;d like to visit.
                  </p>
                </div>
                <div className="space-y-3">
                  {centres.map((centre, i) => (
                    <motion.button
                      key={centre.id}
                      custom={i}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      onClick={() => handleSelectCentre(centre)}
                      className="w-full text-left p-4 rounded-xl border border-white/8 bg-white/3 hover:border-primary/30 hover:bg-white/5 transition-all duration-300 group"
                      aria-label={`Select ${centre.name}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                            <Building2 size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-white">{centre.name}</p>
                            <p className="text-xs text-white/40 mt-0.5">{centre.city}{centre.locality ? ` · ${centre.locality}` : ''}</p>
                          </div>
                        </div>
                        <ChevronRight size={14} className="text-white/20 group-hover:text-primary/60 transition-colors" />
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* STEP 2: SELECT CARE CATEGORY */}
            {step === 'select-category' && (
              <motion.div key="select-category" variants={panelVariants} initial="enter" animate="center" exit="exit">
                <div className="mb-8">
                  <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2">
                    WHAT DO YOU NEED HELP WITH?
                  </h1>
                  <p className="text-sm text-white/50">
                    Choose the area of care that best describes your need.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {careCategories.map((cat, i) => {
                    const CatIcon = categoryIconMap[cat.id] ?? Stethoscope;
                    const colors = categoryColorMap[cat.id] ?? { text: 'text-primary', border: 'border-primary/30', bg: 'bg-primary/8' };
                    return (
                      <motion.button
                        key={cat.id}
                        custom={i}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        onClick={() => handleSelectCategory(cat)}
                        className={`text-left p-4 rounded-xl border ${colors.border} ${colors.bg} hover:border-opacity-60 hover:scale-[1.01] transition-all duration-250 group`}
                        aria-label={`Select ${cat.label}`}
                      >
                        <div className="flex items-center gap-3">
                          <CatIcon size={18} className={colors.text} />
                          <div>
                            <p className={`text-xs font-bold tracking-wider ${colors.text}`}>{cat.label}</p>
                            <p className="text-xs text-white/35 mt-0.5 leading-tight">{cat.description}</p>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 3: SELECT DEPARTMENT */}
            {step === 'select-department' && selectedCategory && (
              <motion.div key="select-department" variants={panelVariants} initial="enter" animate="center" exit="exit">
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    {(() => {
                      const CatIcon = categoryIconMap[selectedCategory.id] ?? Stethoscope;
                      const colors = categoryColorMap[selectedCategory.id] ?? { text: 'text-primary' };
                      return <CatIcon size={16} className={colors.text} />;
                    })()}
                    <span className="text-xs text-white/40 tracking-wider">{selectedCategory.label}</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2">
                    SELECT DEPARTMENT
                  </h1>
                  <p className="text-sm text-white/50">
                    Choose the specific department for your care.
                  </p>
                </div>
                <div className="space-y-3">
                  {availableDepts.map((dept, i) => {
                    const available = selectedCentre
                      ? isDepartmentAvailableAtCentre(selectedCentre.id, dept.id)
                      : true;
                    return (
                      <motion.button
                        key={dept.id}
                        custom={i}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        onClick={() => handleSelectDept(dept)}
                        className={`w-full text-left p-4 rounded-xl border transition-all duration-300 group ${
                          available
                            ? 'border-white/8 bg-white/3 hover:border-primary/30 hover:bg-white/5' :'border-white/5 bg-white/2 opacity-60'
                        }`}
                        aria-label={`Select ${dept.officialName}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-white">{dept.officialName}</p>
                            {!available && (
                              <p className="text-xs text-amber-400/70 mt-0.5">May not be available at {selectedCentre?.shortName}</p>
                            )}
                          </div>
                          <ChevronRight size={14} className="text-white/20 group-hover:text-primary/60 transition-colors" />
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 4: SELECT DOCTOR */}
            {step === 'select-doctor' && selectedDept && (
              <motion.div key="select-doctor" variants={panelVariants} initial="enter" animate="center" exit="exit">
                <div className="mb-8">
                  <p className="text-xs text-white/40 tracking-wider mb-2">{selectedDept.officialName}</p>
                  <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2">
                    SELECT DOCTOR
                  </h1>
                  <p className="text-sm text-white/50">
                    Verified doctors available for {selectedDept.officialName}.
                  </p>
                </div>

                {deptDoctors.length > 0 ? (
                  <div className="space-y-4">
                    {deptDoctors.map((doc, i) => (
                      <motion.div
                        key={doc.id}
                        custom={i}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        className="p-5 rounded-xl border border-white/8 bg-white/3 hover:border-primary/25 hover:bg-white/5 transition-all duration-300"
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                            style={{ background: doc.avatarColor }}
                            aria-hidden="true"
                          >
                            {doc.initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-bold text-white">{doc.name}</h3>
                            <p className="text-xs text-primary/80 mt-0.5">{doc.specialty}</p>
                            <p className="text-xs text-white/40 mt-0.5">{doc.qualifications}</p>
                            {doc.experience && (
                              <p className="text-xs text-white/30 mt-0.5">{doc.experience} experience</p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-3">
                              <button
                                onClick={() => router.push(`/doctor-profile-screen?doctorId=${doc.id}`)}
                                className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                                aria-label={`View profile of ${doc.name}`}
                              >
                                VIEW PROFILE
                                <ChevronRight size={11} />
                              </button>
                              <button
                                onClick={() =>
                                  router.push(
                                    `/consultation-booking?doctorId=${doc.id}&departmentId=${selectedDept?.id ?? doc.department}`
                                  )
                                }
                                className="flex items-center gap-1.5 text-xs font-semibold text-white/50 hover:text-white/80 transition-colors ml-3"
                                aria-label={`Request consultation with ${doc.name}`}
                              >
                                REQUEST CONSULTATION
                                <ChevronRight size={11} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 rounded-xl border border-white/8 bg-white/3 text-center">
                    <UserRound size={32} className="text-white/20 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-white/60 mb-1">DOCTOR PROFILES</p>
                    <p className="text-xs text-white/30">
                      Verified doctor profiles for {selectedDept.officialName} will be available soon.
                    </p>
                    <p className="text-xs text-primary/50 mt-3 tracking-wider">COMING SOON</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* NOT AVAILABLE */}
            {step === 'not-available' && selectedDept && (
              <motion.div key="not-available" variants={panelVariants} initial="enter" animate="center" exit="exit">
                <div className="p-6 rounded-xl border border-amber-500/20 bg-amber-500/5 mb-6">
                  <AlertCircle size={24} className="text-amber-400 mb-3" />
                  <h2 className="text-base font-bold text-white mb-2">
                    THIS SERVICE MAY NOT BE AVAILABLE AT THIS CENTRE
                  </h2>
                  <p className="text-sm text-white/50">
                    <strong className="text-white/70">{selectedDept.officialName}</strong> may not currently be available at{' '}
                    <strong className="text-white/70">{selectedCentre?.name}</strong>.
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => setLocationOpen(true)}
                    className="w-full flex items-center justify-between p-4 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-all"
                    aria-label="Change centre"
                  >
                    <div className="flex items-center gap-3">
                      <MapPin size={16} className="text-primary" />
                      <span className="text-sm font-semibold text-white">CHANGE CENTRE</span>
                    </div>
                    <ChevronRight size={14} className="text-primary/50" />
                  </button>
                  <button
                    onClick={() => setStep('select-category')}
                    className="w-full flex items-center justify-between p-4 rounded-xl border border-white/8 bg-white/3 hover:border-white/15 transition-all"
                    aria-label="Explore other care options"
                  >
                    <div className="flex items-center gap-3">
                      <Stethoscope size={16} className="text-white/50" />
                      <span className="text-sm font-semibold text-white/70">EXPLORE OTHER CARE OPTIONS</span>
                    </div>
                    <ChevronRight size={14} className="text-white/20" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <LocationFinder
        isOpen={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSelectCentre={(centre) => {
          setSelectedCentre(centre);
          setLocationOpen(false);
          setStep('select-category');
        }}
        selectedCentreId={selectedCentre?.id}
      />
    </main>
  );
}
